import type { TemplateResult } from 'lit';
import type {
  LoginDomainScopeGrant,
  LoginGroup,
  LoginGroupImis,
} from '../../../api/agwa-api-model';
import type { UserProfile } from '../../../datatypes/user-profile';

export interface MemberWorkspaceContext {
  group: LoginGroup | null;
  imis: LoginGroupImis | null;
  capabilityProfile: UserProfile | null;
  securityError: string | null;
  loading: boolean;
  domains: LoginDomainScopeGrant[];
  /**
   * The operation-type codes the company holds BY INHERITANCE: the union of its active sites'
   * active operation types. Operation types are site-scoped, so a company's own `domains`
   * list is empty (observed on DEV GLASS PROCESSOR, 2026-09-06) and this is the only way a
   * company-level tab can know what the company is.
   */
  inheritedDomains: string[];
  refreshSecurity: () => Promise<void>;
}

export interface MemberWorkspaceModuleDescriptor {
  id: string;
  label: string;
  icon?: string;
  order?: number;
  /** Group-level domain codes; the tab shows when the company itself holds any of them. */
  domains?: string[];
  /**
   * Site-level operation-type codes; the tab shows when ANY of the company's sites holds one
   * (a company is "a glass processor by inheritance", architect 2026-09-06). Either list
   * matching shows the tab.
   */
  inheritedDomains?: string[];
  /**
   * A further reason to show the tab, beyond the two domain gates, that needs a read the
   * workspace does not make itself: the product registry shows its tabs to AGWA staff for a
   * company whose register has gone inactive (D-146), which no domain list can say. Asked once
   * per company when the workspace opens and again whenever its sites' domains change; the tab
   * shows when either a domain gate or this answers yes. A rejected promise counts as no.
   */
  alsoShowWhen?: (groupId: string) => Promise<boolean>;
  systemOnly?: boolean;
  /** Optional section header to group this tab under (e.g., "Configuration") */
  section?: string;
  load?: () => Promise<unknown>;
  render: (context: MemberWorkspaceContext) => TemplateResult;
}

const moduleRegistry = new Map<string, MemberWorkspaceModuleDescriptor>();

export function registerMemberWorkspaceModule(
  descriptor: MemberWorkspaceModuleDescriptor
) {
  if (!descriptor.id || moduleRegistry.has(descriptor.id)) {
    throw new Error(
      `registerMemberWorkspaceModule: a module with id "${descriptor.id}" already exists.`
    );
  }
  moduleRegistry.set(descriptor.id, descriptor);
}

export function unregisterMemberWorkspaceModule(id: string) {
  moduleRegistry.delete(id);
}

export function listMemberWorkspaceModules() {
  return Array.from(moduleRegistry.values()).sort((a, b) => {
    const orderA = a.order ?? 100;
    const orderB = b.order ?? 100;
    if (orderA === orderB) {
      return a.label.localeCompare(b.label);
    }
    return orderA - orderB;
  });
}
